import { Locale } from "@tableyeti/merchant-service/api.ts";
import { computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useYetipayApi } from "./useYetipayApi";
import { useApplicationStore } from "@/store/application";
import { useAuthenticationStore } from "@/store/authentication";
import { useMerchantsStore } from "@/store/merchants";

export const useLocale = () => {
  const { locale } = useI18n();
  const authStore = useAuthenticationStore();
  const isLoggedIn = authStore?.user?.isAuthenticated;
  const { api, currentMerchantId } = isLoggedIn
    ? useYetipayApi()
    : { api: null, currentMerchantId: null };
  const appStore = useApplicationStore();

  const getLocaleName = (language: string) => {
    if (language === "es-ES") return "Spanish";
    if (language === "fr-FR") return "French";
    if (language === "it-IT") return "Italian";
    return "English";
  };

  const updateLocale = async (newLocale: Locale) => {
    try {
      if (currentMerchantId) {
        await api.updateUser(currentMerchantId, authStore.user.id, {
          preferences: {
            locale: newLocale,
          },
        });
      }
      locale.value = newLocale;
      localStorage.setItem("locale", newLocale);
      window.location.reload();
    } catch (e) {
      console.error(e);
      appStore.notifyUser({ message: "Failed to update language.", type: "error" });
    }
  };

  const handleLocaleOnMount = async () => {
    // For some reason I need to create a new instance of the api here since the top scope one is null when this method is called
    const { api: yetiApi, currentMerchantId } = useYetipayApi();

    if (currentMerchantId) {
      const response = await yetiApi.getUser(currentMerchantId, authStore.user.id);
      const user = response.data;
      const userPreferencesLocale = user.preferences?.locale;
      if (userPreferencesLocale && userPreferencesLocale !== locale.value) {
        locale.value = userPreferencesLocale;
        localStorage.setItem("locale", userPreferencesLocale);
        window.location.reload();
      }
    }
  };

  const merchantsStore = useMerchantsStore();
  const merchantLoaded = computed(() => !!merchantsStore.currentMerchant?.merchantId);
  watch(merchantLoaded, (value) => {
    if (value) {
      handleLocaleOnMount();
    }
  });

  return {
    locale,
    updateLocale,
    getLocaleName,
  };
};
